import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button, Form, Image, Input, Modal, Segmented, Select, Spin, notification } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  FaChevronLeft,
  FaFileMedical,
  FaImage,
  FaPills,
  FaNotesMedical,
  FaStethoscope,
  FaCircleCheck,
} from "react-icons/fa6";
import DataProvider from "../../apis";
import type {
  DoctorReportItem,
  CreateReportPayload,
  CreateReportEPrescriptionPayload,
} from "../../apis";
import LoadWrapper from "../../components/elements/load-wrapper";
import UploadPrescription from "../../components/sections/upload-prescription";
import AddMedicationModal, {
  type MedicationFormValues,
} from "../../components/prescriptions/add-medication-modal";
import { E_PRESCRIPTIONS_BASE } from "../../constants/e-prescriptions";
import Colors from "../../constants/Colors";
import { REGISTRATION_PLATFORM_DOCTOR_WEBSITE } from "../../constants/registrationPlatform";

// Mirrors new-doctor-app's app/(tabs)/appointments/[id]/report.tsx exactly:
// a report is created once (recommendations required; either an uploaded
// prescription image OR a structured e-prescription medication draft, never
// both), then becomes read-only — the backend rejects PATCH on this route
// unconditionally, so there's no "update report" path here anymore. A
// drafted medication list stays editable client-side after creation, but
// only actually becomes a real DoctorPrescription once the doctor
// explicitly confirms "Generate e-prescription" (a second, separate API
// call — POST /appointments/report/:id/eprescription).

interface InsuranceProviderOption {
  id: number;
  name: string;
}

type GenerateFormValues = {
  patientEmail?: string;
  patientLocation?: string;
  insuranceProvider?: string;
  policyNumber?: string;
};

export default function DoctorReport() {
  const [api, contextHolder] = notification.useNotification();
  const navigate = useNavigate();
  const params = useParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<DoctorReportItem | null>(null);

  // Create-mode only — once `data` exists these three are frozen/read-only.
  const [mode, setMode] = useState<"eprescription" | "upload">("eprescription");
  const [prescription, setPrescription] = useState<string | null>(null);
  const [recommendations, setRecommendations] = useState<string>("");
  const [remarks, setRemarks] = useState<string>("");
  const [diagnosis, setDiagnosis] = useState<string>("");

  // The e-prescription draft — built up before the report exists, then
  // still editable (add/edit/delete) after creation right up until
  // "Generate e-prescription" is confirmed, matching new-doctor-app's
  // ReportSummary reusing the same medication sheet for edits.
  const [medications, setMedications] = useState<MedicationFormValues[]>([]);
  const [medicationModalOpen, setMedicationModalOpen] = useState(false);
  const [editingMedicationIndex, setEditingMedicationIndex] = useState<number | null>(null);

  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateForm] = Form.useForm<GenerateFormValues>();

  const [insuranceOptions, setInsuranceOptions] = useState<{ label: string; value: string }[]>([]);
  const [insuranceOptionsLoading, setInsuranceOptionsLoading] = useState(true);
  const [insuranceOptionsError, setInsuranceOptionsError] = useState(false);

  const loadInsuranceOptions = useCallback(() => {
    setInsuranceOptionsLoading(true);
    setInsuranceOptionsError(false);
    DataProvider.getPartners("filter=type||eq||insurance&limit=200")
      .then((res) => {
        const raw = res.data;
        const list: InsuranceProviderOption[] = Array.isArray(raw)
          ? raw
          : (raw as { data?: InsuranceProviderOption[] })?.data ?? [];
        setInsuranceOptions(list.map((p) => ({ label: p.name, value: p.name })));
      })
      .catch(() => setInsuranceOptionsError(true))
      .finally(() => setInsuranceOptionsLoading(false));
  }, []);

  useEffect(() => {
    loadInsuranceOptions();
  }, [loadInsuranceOptions]);

  const onFetch = useCallback(async (id: string | undefined) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data: res } = await DataProvider.getReport(id);
      if (res) {
        setData(res);
        setDiagnosis(res.diagnosis ?? "");
        setRecommendations(res.recommendations ?? "");
        setRemarks(res.remarks ?? "");
        setPrescription(res.prescription ?? null);
        setMedications(res.prescriptionMedications ?? []);
      }
    } catch {
      // No report yet — the create form below stays showing.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    onFetch(params.id);
  }, [params.id, onFetch]);

  function getErrorMessage(err: unknown): string {
    if (err && typeof err === "object" && "response" in err) {
      const ax = (err as { response?: { data?: { message?: string | string[] } } }).response;
      const msg = ax?.data?.message;
      if (typeof msg === "string") return msg;
      if (Array.isArray(msg)) return msg[0] ?? "Request failed.";
    }
    if (err instanceof Error) return err.message;
    return "Something went wrong.";
  }

  function toMedicationPayload(list: MedicationFormValues[]) {
    return list
      .filter((m): m is MedicationFormValues & { medicine: string } => !!m.medicine)
      .map((m) => ({
        medicine: m.medicine,
        dose: m.dose || undefined,
        route: m.route || undefined,
        dosage: m.dosage || undefined,
        period: m.period || undefined,
      }));
  }

  async function onSave() {
    if (!recommendations.trim()) {
      api.warning({ message: "Recommendations required", description: "Please add a recommendation." });
      return;
    }
    const id = params.id;
    if (!id) return;
    try {
      setLoading(true);
      const payload: CreateReportPayload = { recommendations };
      if (diagnosis) payload.diagnosis = diagnosis;
      if (remarks) payload.remarks = remarks;
      if (mode === "upload") {
        if (prescription) payload.prescription = prescription;
      } else if (medications.length > 0) {
        payload.prescriptionMedications = toMedicationPayload(medications);
      }
      const { data: res } = await DataProvider.createReport(id, payload);
      if (res) {
        setData(res);
        setDiagnosis(res.diagnosis ?? "");
        setRecommendations(res.recommendations ?? "");
        setRemarks(res.remarks ?? "");
        setPrescription(res.prescription ?? null);
        setMedications(res.prescriptionMedications ?? []);
        api.success({ message: "Saved", description: "Report added successfully." });
      }
    } catch (err) {
      api.error({ message: "Failed", description: getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }

  const openAddMedication = () => {
    setEditingMedicationIndex(null);
    setMedicationModalOpen(true);
  };
  const openEditMedication = (index: number) => {
    setEditingMedicationIndex(index);
    setMedicationModalOpen(true);
  };
  const handleMedicationSubmit = (values: MedicationFormValues) => {
    setMedications((prev) => {
      if (editingMedicationIndex !== null) {
        const next = [...prev];
        next[editingMedicationIndex] = values;
        return next;
      }
      return [...prev, values];
    });
    setMedicationModalOpen(false);
    setEditingMedicationIndex(null);
  };
  const handleMedicationDelete = (index: number) => {
    setMedications((prev) => prev.filter((_, i) => i !== index));
  };

  const openGenerateModal = () => {
    generateForm.resetFields();
    setGenerateModalOpen(true);
  };

  const onGenerate = () => {
    generateForm
      .validateFields()
      .then(async (values) => {
        const id = params.id;
        if (!id) return;
        try {
          setGenerateLoading(true);
          const payload: CreateReportEPrescriptionPayload = {
            medications: toMedicationPayload(medications),
            patientEmail: values.patientEmail || undefined,
            patientLocation: values.patientLocation || undefined,
            insuranceProvider: values.insuranceProvider || undefined,
            policyNumber: values.policyNumber || undefined,
            source: REGISTRATION_PLATFORM_DOCTOR_WEBSITE,
          };
          const { data: prescriptionRes } = await DataProvider.generateEPrescriptionFromReport(id, payload);
          setData((prev) => (prev ? { ...prev, doctorPrescription: prescriptionRes } : prev));
          setGenerateModalOpen(false);
          api.success({
            message: "E-prescription generated",
            description: "The prescription is now official and will be processed for delivery.",
          });
        } catch (err) {
          api.error({ message: "Failed to generate", description: getErrorMessage(err) });
        } finally {
          setGenerateLoading(false);
        }
      })
      .catch(() => {
        // antd validation errors already shown inline on the fields.
      });
  };

  const fieldClass =
    "w-full rounded-xl border border-slate-200 bg-white p-3.5 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition-colors focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/20 resize-y min-h-[88px]";
  const readOnlyValueClass = "rounded-xl border border-slate-100 bg-slate-50/60 p-3.5 text-sm text-slate-700 whitespace-pre-wrap min-h-[52px]";

  // Once a report exists, whether it's "the upload one" or "the
  // e-prescription one" is derived from what's actually on it, not a
  // doctor-facing toggle anymore — an uploaded image freezes that path;
  // anything else (including a report saved with zero medications so
  // far) still has the e-prescription path open.
  const isUploadReport = !!data?.prescription;
  const isGenerated = !!data?.doctorPrescription;

  return (
    <div className="min-h-full bg-medpharma-mist/40">
      {contextHolder}

      {/* Page header */}
      <header className="mb-6">
        <Button
          type="text"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-600 hover:!text-primary-blue hover:!bg-primary-blue/5 -ml-2 mb-3"
          aria-label="Go back to appointment"
        >
          <FaChevronLeft aria-hidden />
          <span>Back</span>
        </Button>
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-blue/10 text-primary-blue">
            <FaFileMedical className="text-xl" aria-hidden />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-800 tracking-tight">
              Doctor report
            </h1>
            <p className="mt-0.5 text-sm text-slate-500">
              {data
                ? "Reports are final once created — the e-prescription draft below can still be edited until it's generated."
                : "Create the consultation report for this appointment"}
            </p>
          </div>
        </div>
      </header>

      <LoadWrapper loading={loading}>
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Left: prescription (upload image, or the e-prescription draft/result) */}
          <div className="lg:col-span-2">
            <section
              className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-card"
              aria-label="Prescription"
            >
              <div className="flex items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-2">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                    {isUploadReport || (!data && mode === "upload") ? (
                      <FaImage className="text-base" aria-hidden />
                    ) : (
                      <FaPills className="text-base" aria-hidden />
                    )}
                  </div>
                  <h2 className="font-semibold text-slate-800">
                    {isUploadReport || (!data && mode === "upload") ? "Prescription image" : "E-prescription"}
                  </h2>
                </div>
                {/* Mode toggle — create-only; once a report exists, what it
                    actually is (image vs structured) is fixed. */}
                {!data && (
                  <Segmented
                    size="small"
                    value={mode}
                    onChange={(v) => setMode(v as "eprescription" | "upload")}
                    options={[
                      { label: "E-prescription", value: "eprescription" },
                      { label: "Upload", value: "upload" },
                    ]}
                  />
                )}
              </div>

              {(data ? isUploadReport : mode === "upload") ? (
                prescription ? (
                  <div className="rounded-xl overflow-hidden border border-slate-200/80 bg-slate-50/50">
                    <Image width="100%" className="rounded-xl" src={prescription} alt="Uploaded prescription" />
                    {!data && (
                      <div className="p-3 border-t border-slate-100">
                        <Button
                          type="text"
                          size="small"
                          className="text-primary-blue hover:!bg-primary-blue/5"
                          onClick={() => setPrescription(null)}
                        >
                          Replace image
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-6 min-h-[220px] flex items-center justify-center">
                    <UploadPrescription prescription={prescription} setPrescription={setPrescription} />
                  </div>
                )
              ) : (
                <div className="space-y-3">
                  {medications.length === 0 ? (
                    <p className="text-sm text-slate-500">No medications added yet.</p>
                  ) : (
                    <div className="rounded-xl border border-slate-200/80 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr className="text-left text-slate-500">
                            <th className="px-3 py-2 font-medium">Medicine</th>
                            <th className="px-3 py-2 font-medium">Dose</th>
                            <th className="px-3 py-2 font-medium">Route</th>
                            <th className="px-3 py-2 font-medium">Dosage</th>
                            <th className="px-3 py-2 font-medium">Period</th>
                            {!isGenerated && <th className="px-3 py-2" />}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {medications.map((m, i) => (
                            <tr key={i} className="text-slate-700">
                              <td className="px-3 py-2 font-medium text-slate-800">{m.medicine}</td>
                              <td className="px-3 py-2">{m.dose || "—"}</td>
                              <td className="px-3 py-2">{m.route || "—"}</td>
                              <td className="px-3 py-2">{m.dosage || "—"}</td>
                              <td className="px-3 py-2">{m.period || "—"}</td>
                              {!isGenerated && (
                                <td className="px-3 py-2">
                                  <div className="flex items-center gap-2 justify-end">
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<EditOutlined />}
                                      aria-label={`Edit ${m.medicine}`}
                                      onClick={() => openEditMedication(i)}
                                    />
                                    <Button
                                      type="text"
                                      size="small"
                                      danger
                                      icon={<DeleteOutlined />}
                                      aria-label={`Remove ${m.medicine}`}
                                      onClick={() => handleMedicationDelete(i)}
                                    />
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {!isGenerated && (
                    <Button
                      type="dashed"
                      block
                      icon={<PlusOutlined />}
                      onClick={openAddMedication}
                      aria-label="Add medication"
                    >
                      Add medication
                    </Button>
                  )}

                  {data && !isGenerated && (
                    <Button
                      type="primary"
                      block
                      size="large"
                      className="!bg-primary-blue hover:!bg-primary-blue/90 font-semibold mt-2"
                      onClick={openGenerateModal}
                      disabled={medications.length === 0}
                    >
                      Generate e-prescription
                    </Button>
                  )}

                  {isGenerated && data?.doctorPrescription && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3.5 text-sm text-emerald-800 flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2">
                        <FaCircleCheck aria-hidden />
                        E-prescription generated — official, order #{data.doctorPrescription.orderId}.
                      </span>
                      <Link
                        to={`${E_PRESCRIPTIONS_BASE}/${data.doctorPrescription.id}`}
                        className="font-semibold text-emerald-700 hover:text-emerald-900 shrink-0"
                      >
                        View
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>

          {/* Right: diagnosis, remarks, recommendations */}
          <div className="lg:col-span-3">
            <section
              className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-card"
              aria-label="Report details"
            >
              <div className="flex items-center gap-2 mb-5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-blue/10 text-primary-blue">
                  <FaStethoscope className="text-base" aria-hidden />
                </div>
                <h2 className="font-semibold text-slate-800">Report details</h2>
              </div>

              <div className="space-y-5">
                <div>
                  <label
                    htmlFor="doctor-report-diagnosis"
                    className="mb-1.5 flex items-center gap-2 text-sm font-medium text-slate-700"
                  >
                    <FaNotesMedical className="text-slate-400 shrink-0" aria-hidden />
                    Diagnosis
                  </label>
                  {data ? (
                    <div className={readOnlyValueClass}>{diagnosis || "—"}</div>
                  ) : (
                    <textarea
                      id="doctor-report-diagnosis"
                      className={fieldClass}
                      rows={3}
                      value={diagnosis}
                      onChange={(e) => setDiagnosis(e.target.value)}
                      placeholder="Enter diagnosis…"
                    />
                  )}
                </div>

                <div>
                  <label
                    htmlFor="doctor-report-remarks"
                    className="mb-1.5 flex items-center gap-2 text-sm font-medium text-slate-700"
                  >
                    Remarks
                  </label>
                  {data ? (
                    <div className={readOnlyValueClass}>{remarks || "—"}</div>
                  ) : (
                    <textarea
                      id="doctor-report-remarks"
                      className={fieldClass}
                      rows={3}
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Additional remarks…"
                    />
                  )}
                </div>

                <div>
                  <label
                    htmlFor="doctor-report-recommendations"
                    className="mb-1.5 flex items-center gap-2 text-sm font-medium text-slate-700"
                  >
                    Recommendations
                    {!data && (
                      <span className="rounded bg-primary-blue/10 px-1.5 py-0.5 text-xs font-medium text-primary-blue">
                        Required
                      </span>
                    )}
                  </label>
                  {data ? (
                    <div className={readOnlyValueClass}>{recommendations || "—"}</div>
                  ) : (
                    <textarea
                      id="doctor-report-recommendations"
                      className={fieldClass}
                      rows={3}
                      value={recommendations}
                      onChange={(e) => setRecommendations(e.target.value)}
                      placeholder="Treatment and follow-up recommendations…"
                      aria-required="true"
                    />
                  )}
                </div>
              </div>
            </section>

            {/* Save CTA — create-only; nothing to save once a report exists. */}
            {!data && (
              <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-2xl border border-slate-200/80 bg-white px-5 py-4 shadow-card">
                <p className="text-sm text-slate-500">
                  Recommendations are required to save the report. This can't be edited afterward.
                </p>
                <Button
                  type="primary"
                  size="large"
                  onClick={onSave}
                  loading={loading}
                  className="!bg-primary-blue hover:!bg-primary-blue/90 font-semibold shrink-0 sm:min-w-[180px]"
                  icon={<FaCircleCheck className="mr-2" aria-hidden />}
                >
                  {loading ? "Saving…" : "Save report"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </LoadWrapper>

      <AddMedicationModal
        open={medicationModalOpen}
        onCancel={() => {
          setMedicationModalOpen(false);
          setEditingMedicationIndex(null);
        }}
        onSubmit={handleMedicationSubmit}
        initialValues={editingMedicationIndex !== null ? medications[editingMedicationIndex] : undefined}
      />

      <GenerateEPrescriptionModal
        open={generateModalOpen}
        loading={generateLoading}
        form={generateForm}
        medications={medications}
        insuranceOptions={insuranceOptions}
        insuranceOptionsLoading={insuranceOptionsLoading}
        insuranceOptionsError={insuranceOptionsError}
        onRetryInsuranceOptions={loadInsuranceOptions}
        onCancel={() => setGenerateModalOpen(false)}
        onConfirm={onGenerate}
      />
    </div>
  );
}

interface GenerateEPrescriptionModalProps {
  open: boolean;
  loading: boolean;
  form: ReturnType<typeof Form.useForm<GenerateFormValues>>[0];
  medications: MedicationFormValues[];
  insuranceOptions: { label: string; value: string }[];
  insuranceOptionsLoading: boolean;
  insuranceOptionsError: boolean;
  onRetryInsuranceOptions: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * "Make this official?" — new-doctor-app's confirm step and its
 * GenerateDetailsSheet combined into one dialog (a single web modal
 * covers both without an extra click). Same 4 optional fields the
 * standalone e-prescription screen offers, plus a read-only recap of the
 * medications about to be sent (already editable on the page itself,
 * not re-editable in here).
 */
function GenerateEPrescriptionModal({
  open,
  loading,
  form,
  medications,
  insuranceOptions,
  insuranceOptionsLoading,
  insuranceOptionsError,
  onRetryInsuranceOptions,
  onCancel,
  onConfirm,
}: GenerateEPrescriptionModalProps) {
  const insuranceProvider = Form.useWatch("insuranceProvider", form);

  return (
    <Modal
      title="Generate e-prescription?"
      open={open}
      onOk={onConfirm}
      onCancel={onCancel}
      okText={loading ? "Generating…" : "Generate e-prescription"}
      cancelText="Cancel"
      confirmLoading={loading}
      width={480}
      destroyOnClose
      // See AddMedicationModal's own comment — Tailwind's preflight
      // strips antd's default primary-button background in this app.
      okButtonProps={{ style: { background: Colors.primary, borderColor: Colors.primary } }}
    >
      <p className="text-sm text-slate-600 mb-4">
        This turns the {medications.length} drafted medication{medications.length === 1 ? "" : "s"} into a real,
        official prescription — MedPharma will process and deliver it. This can't be undone.
      </p>
      <Form layout="vertical" form={form} requiredMark={false}>
        <Form.Item<GenerateFormValues> label="Patient email (optional)" name="patientEmail" rules={[{ type: "email", message: "Enter a valid email" }]}>
          <Input type="email" placeholder="Overrides the appointment's own patient email, if set" />
        </Form.Item>
        <Form.Item<GenerateFormValues> label="Delivery address (optional)" name="patientLocation">
          <Input.TextArea placeholder="Where the medications should be delivered" rows={2} />
        </Form.Item>
        <Form.Item<GenerateFormValues>
          label="Insurance provider (optional)"
          name="insuranceProvider"
          extra={
            insuranceOptionsError ? (
              <span className="text-red-500 text-xs">
                Could not load providers.{" "}
                <button type="button" onClick={onRetryInsuranceOptions} className="text-red-500 underline">
                  Try again
                </button>
              </span>
            ) : undefined
          }
        >
          <Select
            placeholder="Select insurance provider"
            options={insuranceOptions}
            loading={insuranceOptionsLoading}
            showSearch
            allowClear
            optionFilterProp="label"
            notFoundContent={insuranceOptionsLoading ? <Spin size="small" /> : "No providers found"}
          />
        </Form.Item>
        <Form.Item<GenerateFormValues>
          label="Policy number"
          name="policyNumber"
          rules={[{ required: !!insuranceProvider, message: "Required when an insurance provider is set" }]}
        >
          <Input placeholder="e.g. NHIS-0012345" disabled={!insuranceProvider} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
